import { useDateTimeDisplay } from '../../../util/useDateTimeDisplay';
import * as React from "react";
import {
  Box,
  Typography,
  Grid,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Button,
  Drawer,
  AppBar,
  Toolbar,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Paper,
  ListSubheader,
  CircularProgress,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import BusinessIcon from "@mui/icons-material/Business";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ImageIcon from "@mui/icons-material/Image";
import PaymentIcon from "@mui/icons-material/Payment";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import DescriptionIcon from "@mui/icons-material/Description";
import SecurityIcon from "@mui/icons-material/Security";
import LocalPrintshopIcon from "@mui/icons-material/LocalPrintshop";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";

import { useMutation, useQuery } from "@apollo/client";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import BusinessForm from "./businessForm";
import WorkingHourView from "../workingHourView";
import ImageEntryForm from "../image/imageEntryForm";
import PaymentSettings from "../../../../products/schedulingBookings/pages/customer/booking/paymentSettings";
import BusinessDocumentsTab from "./businessDocumentsTab";
import BusinessRoleBuilderTab from "./businessRoleBuilderTab";



import { UserContext } from "../../auth/auth-context";
import { StyledGrid } from "../../../assets/css/styledComponent";
import { useStyles } from "./styles";
import { useSelectedBusiness } from "../../../redux/store";
import { EntityType } from "../../../redux/actions/businessActions";
import { getEffectivePlan, useIsMobile } from "../../../util/utils";
import { hasStandaloneBookingAccess } from "../../../../core/business/productAccess";
import {
  detectVerticalFromPath,
  isVerticalId,
} from "../../../verticals/registry";
import { getStoredVertical } from "../../../routes/verticalPref";
import { getBrandVertical } from "../../../util/brandDomains";

import {
  CREATE_STRIPE_SUBSCRIPTION,
  GET_BILLING_SUBSCRIPTION,
  GET_PLANS,
} from "../../../graphql/queries";

function getPlanTierCode(rawCode?: string | null) {
  const code = String(rawCode || "").trim().toUpperCase();
  if (code.startsWith("BOOKING_")) return code.replace("BOOKING_", "");
  if (code.startsWith("CARE_")) return code.replace("CARE_", "");
  return code;
}

/* ================= TAB PANEL ================= */

function TabPanel({
  children,
  value,
  index,
}: {
  children?: React.ReactNode;
  value: number;
  index: number;
}) {
  if (value !== index) return null;
  return <Box sx={{ minWidth: 0 }}>{children}</Box>;
}

/* ================= COMPONENT ================= */

export const BusinessDetailHorizontalTab: React.FC<{
  isDisabled: boolean;
  isNewBusiness?: boolean;
}> = ({ isDisabled, isNewBusiness = false }) => {
  const { dateLabel } = useDateTimeDisplay();
  const { tabId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const classes = useStyles();
  const isMobile = useIsMobile();

  const { providerUser: token } = React.useContext(UserContext);
  const selectedBusiness = useSelectedBusiness();
  const businessStaffs = selectedBusiness?.businessStaffs || [];
  const currentLoginStaff = businessStaffs.find(
    (staff: any) => Number(staff?.id) === Number(token?.staffId)
  );

  const businessId =
    selectedBusiness?.businessBasic?.id ??
    token?.defaultBusinessId ??
    null;
  const effectivePlan = getEffectivePlan(token);
  const canUseStandaloneBooking = hasStandaloneBookingAccess(token);
  const activeProductId = "booking";
  const billingProductKey = "booking";

  const [value, setValue] = React.useState(tabId ? Number(tabId) : 0);
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const [selectedPlanId, setSelectedPlanId] = React.useState<string | null>(null);

  React.useEffect(() => {
    const parsedTab = Number(tabId);
    if (Number.isInteger(parsedTab) && parsedTab >= 0 && parsedTab <= 8 && parsedTab !== value) {
      setValue(parsedTab);
    }
    if ((tabId == null || tabId === "") && value !== 0) {
      setValue(0);
    }
  }, [tabId, value]);

  const selectTab = React.useCallback(
    (nextIndex: number) => {
      setValue(nextIndex);
      const path = `/biz/business/${nextIndex}${location.search || ""}${location.hash || ""}`;
      navigate(path, { replace: true });
    },
    [navigate, location.search, location.hash]
  );

  /* ================= DATA ================= */

  const { data: plansData } = useQuery(GET_PLANS, {
    variables: { product: billingProductKey },
  });

  const { data: subData, loading: loadingSub } = useQuery(
    GET_BILLING_SUBSCRIPTION,
    {
      variables: { businessId, productKey: billingProductKey },
      skip: !businessId,
      fetchPolicy: "network-only",
    }
  );

  const [createStripeSubscription, { loading: redirecting }] =
    useMutation(CREATE_STRIPE_SUBSCRIPTION);

  const subscription = subData?.getBillingSubscription;
  const freePlan = plansData?.plans?.find(
    (plan: any) => getPlanTierCode(plan.code) === "FREE"
  );
  const freePlanId = freePlan?.id != null ? String(freePlan.id) : null;

  const currentPlanId =
    subscription?.status === "canceled"
      ? freePlanId
      : subscription?.planId != null
        ? String(subscription.planId)
        : freePlanId;
  const firstPaidPlanId = React.useMemo(
    () =>
      plansData?.plans?.find(
        (plan: any) => getPlanTierCode(plan?.code) !== "FREE"
      )?.id != null
        ? String(
            plansData.plans.find(
              (plan: any) => getPlanTierCode(plan?.code) !== "FREE"
            )?.id
          )
        : null,
    [plansData?.plans]
  );
  const seatsPurchased = Number(subscription?.residentSeats ?? 0);
  const seatsUsed = Number(subscription?.residentCount ?? 0);
  const seatsLeft = seatsPurchased - seatsUsed;
  const isBookingBillingContext =
    true;
  const purchasedLabel = "Booking staff seats purchased";
  const usedLabel = "Staff used";
  const leftLabel = "Booking staff seats left";

  const isCanceled =
    subscription?.status === "canceled";

  const isCanceling =
    subscription?.status === "canceling";
  const isTrialing = subscription?.status === "trialing";
  const nextBillingDateLabel = subscription?.nextBillingAt
    ? dateLabel(subscription.nextBillingAt)
    : "";

  /* ================= ACTION ================= */

  const handlePlanAction = async () => {
    if (!businessId || !selectedPlanId) return;
    const fromPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    if (fromPath) {
      localStorage.setItem(
        "pendingUpgradeNavigation",
        JSON.stringify({
          path: fromPath,
          businessId: Number(businessId || 0),
          createdAt: Date.now(),
        })
      );
    }
    const res = await createStripeSubscription({
      variables: {
        businessId,
        planId: selectedPlanId,
        residentCount: seatsPurchased,
        productKey: "booking",
      },
    });

    const checkoutUrl = res?.data?.createStripeSubscription?.checkoutUrl;
    if (checkoutUrl) {
      window.location.href = checkoutUrl;
      return;
    }
    localStorage.removeItem("pendingUpgradeNavigation");

    alert("Plan updated successfully.");
  };

  /* ================= MENU ================= */

  const canManageRoles =
    String(currentLoginStaff?.role?.name || "").trim().toUpperCase() === "ADMIN";
  const canViewAlertRouting =
    false;

  const profileMenuItems = [
    { label: "Business Basic", index: 0, icon: <BusinessIcon fontSize="small" /> },
    { label: "Business Hour", index: 1, icon: <AccessTimeIcon fontSize="small" /> },
    { label: "Images", index: 2, icon: <ImageIcon fontSize="small" /> },
    { label: "Payment Integration", index: 3, icon: <PaymentIcon fontSize="small" /> },
    { label: "Subscription & Billing", index: 4, icon: <ReceiptLongIcon fontSize="small" /> },
    { label: "Documents", index: 5, icon: <DescriptionIcon fontSize="small" /> },

  ].filter((item: any) => item.visible !== false);
  const adminMenuItems = [
    { label: "Roles & Permissions", index: 6, icon: <SecurityIcon fontSize="small" />, visible: canManageRoles },

  ].filter((item: any) => item.visible !== false);
  const allMenuItems = [...profileMenuItems, ...adminMenuItems];

  const allowedTabIndexes = React.useMemo(
    () => allMenuItems.map((item) => item.index),
    [allMenuItems]
  );

  React.useEffect(() => {
    if (!allowedTabIndexes.includes(value)) {
      const fallback = allowedTabIndexes[0] ?? 0;
      if (fallback !== value) selectTab(fallback);
    }
  }, [allowedTabIndexes, value, selectTab]);

  React.useEffect(() => {
    const availablePlanIds = new Set(
      Array.isArray(plansData?.plans)
        ? plansData.plans.map((plan: any) => String(plan?.id))
        : []
    );
    const normalizedSelected = selectedPlanId ? String(selectedPlanId) : "";
    if (normalizedSelected && availablePlanIds.has(normalizedSelected)) {
      return;
    }

    const nextPlanId =
      currentPlanId ||
      firstPaidPlanId ||
      freePlanId ||
      null;
    if (String(nextPlanId || "") !== normalizedSelected) {
      setSelectedPlanId(nextPlanId ? String(nextPlanId) : null);
    }
  }, [plansData?.plans, selectedPlanId, currentPlanId, firstPaidPlanId, freePlanId]);

  const planStatus = isCanceled ? (
    "Switch Plan"
  ) : isCanceling ? (
    "Switch Plan"
  ) : selectedPlanId === freePlanId ? (
    "Switch to Free"
  ) : (
    "Switch Plan"
  )


  /* ================= SUBSCRIPTION UI ================= */

  const SubscriptionContent = () => {
    return (
      <Box sx={{ p: 2 }}>
        <Typography paragraph>
          Manage your subscription and billing. Plan changes are handled securely
          through Stripe.
        </Typography>

        <Typography variant="body2">
          Product: <strong>{"Booking"}</strong>
        </Typography>

        <Typography variant="body2">
          Status: <strong>{subscription?.status || "free / no active paid subscription"}</strong>
        </Typography>

        {!subscription && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            No active {"Booking"} billing subscription is attached to this business yet. You can still choose a plan below.
          </Typography>
        )}

        {isTrialing && nextBillingDateLabel && (
          <Typography variant="body2" color="primary" sx={{ mt: 0.5 }}>
            Trial active until <strong>{nextBillingDateLabel}</strong>
          </Typography>
        )}

        <Typography variant="body2">
          {purchasedLabel}: <strong>{seatsPurchased}</strong>
        </Typography>

        <Typography variant="body2">
          {usedLabel}: <strong>{seatsUsed}</strong>
        </Typography>

        {seatsLeft > 0 && (
          <Typography variant="body2" color="text.secondary">
            {leftLabel}: <strong>{seatsLeft}</strong>
          </Typography>
        )}

        {/* ⚠️ CANCELED WARNING */}
        {isCanceled && (
          <Box
            sx={{
              mt: 2,
              p: 1.5,
              borderRadius: 1,
              bgcolor: "warning.light",
            }}
          >
            <Typography variant="body2" fontWeight={600}>
              Subscription canceled
            </Typography>
            <Typography variant="caption">
              Your current plan and seats will remain active until the end of the
              billing period. After that, your account will downgrade to the Free
              plan and paid features will be disabled.
            </Typography>
          </Box>
        )}

        <FormControl fullWidth margin="normal">
          <InputLabel>Plan</InputLabel>
          <Select
            label="Plan"
            value={selectedPlanId ?? ""}
            onChange={(e) => setSelectedPlanId(String(e.target.value))}
          >
            {plansData?.plans?.map((plan: any) => {
              return (
                <MenuItem
                  key={plan.id}
                  value={plan.id}
                  disabled={
                    String(plan.id) === String(currentPlanId)
                  }
                >
                  {plan.name}
                  {String(plan.id) === String(currentPlanId) && " (current)"}
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>

        <Button
          variant="contained"
          sx={{ mt: 2 }}
          disabled={
            !selectedPlanId ||
            String(selectedPlanId) === String(currentPlanId) ||
            redirecting
          }
          onClick={handlePlanAction}
        >
          {redirecting ? (
            <CircularProgress size={22} />
          ) : (
            !subscription &&
            selectedPlanId &&
            String(selectedPlanId) !== String(freePlanId)
              ? "Start 30-Day Trial"
              : planStatus
          )}
        </Button>
      </Box>
    );
  };

  if (loadingSub) return null;

  /* ================= RENDER ================= */

  return (
    <StyledGrid container spacing={2} sx={{ mt: 0, minWidth: 0 }}>
      {/* LEFT MENU */}
      <StyledGrid item xs={12} md={3}>
        {isMobile ? (
          <>
            <AppBar position="sticky" color="default" elevation={0}>
              <Toolbar>
                <IconButton onClick={() => setDrawerOpen(true)}>
                  <MenuIcon />
                </IconButton>
                <Typography variant="h6">Settings</Typography>
              </Toolbar>
            </AppBar>

            <Drawer open={drawerOpen} onClose={() => setDrawerOpen(false)}>
              <List>
                <ListSubheader>Workspace profile</ListSubheader>
                {profileMenuItems.map((item) => (
                  <ListItemButton
                    key={item.index}
                    selected={value === item.index}
                    onClick={() => {
                      selectTab(item.index);
                      setDrawerOpen(false);
                    }}
                  >
                    <ListItemIcon>{item.icon}</ListItemIcon>
                    <ListItemText primary={item.label} />
                  </ListItemButton>
                ))}
                {adminMenuItems.length > 0 ? (
                  <ListSubheader>Admin Tools</ListSubheader>
                ) : null}
                {adminMenuItems.map((item) => (
                  <ListItemButton
                    key={item.index}
                    selected={value === item.index}
                    onClick={() => {
                      selectTab(item.index);
                      setDrawerOpen(false);
                    }}
                  >
                    <ListItemIcon>{item.icon}</ListItemIcon>
                    <ListItemText primary={item.label} />
                  </ListItemButton>
                ))}
              </List>
            </Drawer>
          </>
        ) : (
          <Paper elevation={0} sx={{ p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 2,
            '& .MuiListItemButton-root': { borderRadius: 1.5, my: 0.25 },
            '& .MuiListItemIcon-root': { minWidth: 36, color: 'text.secondary' },
            '& .Mui-selected .MuiListItemIcon-root': { color: 'primary.main' },
            '& .MuiListItemText-primary': { fontSize: '0.875rem', fontWeight: 500 },
          }}>
            <List
              subheader={
                <ListSubheader sx={{ fontWeight: 700 }}>
                  Workspace profile
                </ListSubheader>
              }
            >
              {profileMenuItems.map((item) => (
                <ListItemButton
                  key={item.index}
                  selected={value === item.index}
                  onClick={() => selectTab(item.index)}
                >
                  <ListItemIcon>{item.icon}</ListItemIcon>
                  <ListItemText primary={item.label} />
                </ListItemButton>
              ))}
            </List>
            {adminMenuItems.length > 0 ? (
              <List
                subheader={
                  <ListSubheader sx={{ fontWeight: 700 }}>
                    Admin Tools
                  </ListSubheader>
                }
              >
                {adminMenuItems.map((item) => (
                  <ListItemButton
                    key={item.index}
                    selected={value === item.index}
                    onClick={() => selectTab(item.index)}
                  >
                    <ListItemIcon>{item.icon}</ListItemIcon>
                    <ListItemText primary={item.label} />
                  </ListItemButton>
                ))}
              </List>
            ) : null}
          </Paper>
        )}
      </StyledGrid>

      {/* RIGHT CONTENT */}
      <Grid item xs={12} md={9} sx={{ minWidth: 0 }}>
        <TabPanel value={value} index={0}>
          <BusinessForm isDisabled={isDisabled} isNewBusiness={isNewBusiness} />
        </TabPanel>

        <TabPanel value={value} index={1}>
          <WorkingHourView
            key={Math.floor(Math.random() * 1000)}
            providerUser={token}
            isDisabled={isDisabled}
            entityType={EntityType.BUSINESS}
            isCustom={false}
            defaultWH={true}
          />
        </TabPanel>

        <TabPanel value={value} index={2}>
          <ImageEntryForm
            singleImageMode={false}
            isDisabled={isDisabled}
            effectivePlan={effectivePlan}
            requiredPlanId={2}
            planImageLimits={{ 2: 5, 3: 10 }}
          />
        </TabPanel>

        <TabPanel value={value} index={3}>
          <PaymentSettings isDisabled={isDisabled} />
        </TabPanel>

        <TabPanel value={value} index={4}>
          <SubscriptionContent />
        </TabPanel>

        <TabPanel value={value} index={5}>
          <BusinessDocumentsTab
            businessId={businessId}
            staffId={token?.staffId}
            isDisabled={isDisabled}
          />
        </TabPanel>

        {canManageRoles && (
          <TabPanel value={value} index={6}>
            <BusinessRoleBuilderTab isDisabled={isDisabled} />
          </TabPanel>
        )}




      </Grid>
    </StyledGrid>
  );
};

export default BusinessDetailHorizontalTab;
